import { getEquippedFor } from './equipped-cache.js';
import { DISPLAY_NAME_FONT_FAMILY, DISPLAY_NAME_FONT_SCALE } from '../../../shared/display-name-fonts.js';

// <retro-username username="..." size="hero|list|compact" display-name="...">
//
// Wraps the displayed username with a nameplate backdrop (Hero/List
// tiers when equipped), a flair icon before the text, and a badge icon
// after the text.
//
// Two ways to provide the displayed text:
//   1. `display-name` attribute (preferred — order-of-operations safe)
//   2. Text node child at connection time (preserved across renders)
// Falls back to the `username` attribute if neither is provided.
//
// Render is split into two phases: the text span is rendered
// synchronously in connectedCallback / attributeChangedCallback so the
// name is always present in the DOM immediately (no flash of empty
// content while the equipped-cache fetch resolves). Equipped relics
// (nameplate background, flair, badge) are layered on after the async
// fetch completes.

class RetroUsername extends HTMLElement {
  static observedAttributes = ['username', 'size', 'display-name'];

  connectedCallback() {
    if (this._originalText === undefined) {
      this._originalText = this.textContent.trim();
    }
    this._renderSync();
    this._applyEquipped();
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (oldVal !== newVal && this.isConnected) {
      this._renderSync();
      this._applyEquipped();
    }
  }

  _renderSync() {
    const username = this.getAttribute('username');
    this.innerHTML = '';
    if (!username) return;
    const displayText = this.getAttribute('display-name')
      || this._originalText
      || username;
    const text = document.createElement('span');
    text.className = 'retro-username-text';
    text.textContent = displayText;
    this.appendChild(text);
    // The equipped lookup is async, so until it resolves (below) we don't yet
    // know whether a nameplate is equipped. Mark the element "loading" so
    // surfaces can reserve the hero plate width and avoid a collapse→plate
    // jump for equipped users (see the gallery title rule). _applyEquipped
    // always follows and overwrites this with the resolved state — for cached
    // users that happens in the same microtask, so there's no visible flash.
    this.dataset.equip = 'loading';
  }

  async _applyEquipped() {
    const username = this.getAttribute('username');
    if (!username) return;
    const equipped = await getEquippedFor(username);
    if (!this.isConnected || this.getAttribute('username') !== username) return;

    const text = this.querySelector('.retro-username-text');
    if (!text) return;

    this.querySelectorAll('.retro-nameplate-bg, .retro-username-flair, .retro-username-badge')
      .forEach((el) => el.remove());

    const nameplateUrl = equipped.nameplate?.image_url;
    const flairUrl = equipped.flair?.image_url;
    const badgeUrl = equipped.badge?.image_url;
    const fontSlug = equipped.display_name_font;
    const size = this.getAttribute('size') || 'list';
    const showNameplate = (size === 'hero' || size === 'list') && nameplateUrl;
    // Resolved. Expose whether a nameplate is actually equipped so CSS can
    // collapse a reserved hero width ONLY for confirmed no-nameplate users,
    // never during the loading window above.
    this.dataset.equip = nameplateUrl ? 'plate' : 'none';

    // Subscriber-only custom font. Server returns null when the
    // subscription isn't active, so a lapsed subscriber silently reverts
    // to the surface default. Each font also carries a `scale` to
    // visually normalise cap-heights and descender depth across the
    // catalog — see DISPLAY_NAME_FONT_SCALE.
    const fontFamily = fontSlug ? DISPLAY_NAME_FONT_FAMILY[fontSlug] : null;
    const fontScale = fontSlug ? DISPLAY_NAME_FONT_SCALE[fontSlug] : null;
    text.style.fontFamily = fontFamily
      ? `"${fontFamily}", var(--font-mono, monospace)`
      : '';
    text.style.fontSize = (fontScale && fontScale !== 1) ? `${fontScale}em` : '';

    if (showNameplate) {
      const bg = document.createElement('span');
      bg.className = 'retro-nameplate-bg';
      bg.style.backgroundImage = `url("${nameplateUrl}")`;
      this.insertBefore(bg, text);
    }

    if (flairUrl) {
      const flair = document.createElement('img');
      flair.className = 'retro-username-flair';
      flair.src = flairUrl;
      flair.alt = '';
      flair.setAttribute('aria-hidden', 'true');
      this.insertBefore(flair, text);
    }

    if (badgeUrl) {
      const badge = document.createElement('img');
      badge.className = 'retro-username-badge';
      badge.src = badgeUrl;
      badge.alt = '';
      badge.setAttribute('aria-hidden', 'true');
      this.appendChild(badge);
    }
  }
}

if (!customElements.get('retro-username')) {
  customElements.define('retro-username', RetroUsername);
}
